/**
 * PATH: packages/drafting/src/canvas/SelectionTransformer.tsx
 * FILE_ID: DRAFTING-CANVAS-TRANSFORMER-001
 * PURPOSE: Resize handles for the selected entity (rect/circle/text). On
 *   transform end it BAKES the Konva node scale back into IR geometry and
 *   resets the node scale to 1, so the IR stays in true drawing units
 *   (never a leftover scaleX/scaleY) — keeping geometry == IR.
 */
import { useEffect, useRef } from 'react'
import { Transformer } from 'react-konva'
import type Konva from 'konva'
import type { Entity } from '../ir/schema'

export interface SelectionTransformerProps {
  selected?: Entity
  layerRef: React.MutableRefObject<Konva.Layer | null>
  onChange: (e: Entity) => void
}

const RESIZABLE = new Set(['rect', 'circle', 'text'])

/** bakeTransform — fold node scale into the entity's own units. */
function bakeTransform(entity: Entity, node: Konva.Node): Entity | null {
  const sx = node.scaleX()
  const sy = node.scaleY()
  switch (entity.type) {
    case 'rect': {
      const w = Math.max(1, (node as Konva.Rect).width() * sx)
      const h = Math.max(1, (node as Konva.Rect).height() * sy)
      node.scaleX(1)
      node.scaleY(1)
      return { ...entity, x: node.x(), y: node.y(), w, h }
    }
    case 'circle': {
      const r = Math.max(1, entity.r * Math.max(sx, sy))
      node.scaleX(1)
      node.scaleY(1)
      return { ...entity, cx: node.x(), cy: node.y(), r }
    }
    case 'text': {
      const size = Math.max(4, (entity.size ?? 12) * sy)
      node.scaleX(1)
      node.scaleY(1)
      return { ...entity, at: [node.x(), node.y()], size, rotation: node.rotation() }
    }
    default:
      return null
  }
}

export function SelectionTransformer(props: SelectionTransformerProps) {
  const { selected, layerRef, onChange } = props
  const trRef = useRef<Konva.Transformer | null>(null)

  useEffect(() => {
    const tr = trRef.current
    const layer = layerRef.current
    if (!tr || !layer) return
    if (selected && RESIZABLE.has(selected.type)) {
      const node = layer.findOne<Konva.Node>(`#${selected.id}`)
      tr.nodes(node ? [node] : [])
    } else {
      tr.nodes([])
    }
    tr.getLayer()?.batchDraw()
  }, [selected, layerRef])

  if (!selected || !RESIZABLE.has(selected.type)) return null

  return (
    <Transformer
      ref={trRef}
      rotateEnabled={selected.type === 'text'}
      // keep tiny shapes grabbable; ignore degenerate flips
      boundBoxFunc={(oldBox, newBox) => (newBox.width < 4 || newBox.height < 4 ? oldBox : newBox)}
      onTransformEnd={(e) => {
        const next = bakeTransform(selected, e.target)
        if (next) onChange(next)
      }}
    />
  )
}
